import NotificationsIcon from "@mui/icons-material/Notifications";
import { IconButton, type SxProps, type Theme } from "@mui/material";
import type React from "react";
import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthProvider";
import { useNotificationsContext } from "../../contexts/NotificationsContext";
import { trackEvent } from "../../utils/analytics";
import { BELL_BADGE_MAX, BellBadge } from "./BellBadge";

type Props = {
	color: "inherit" | "primary" | "secondary" | "default";
	size: "small" | "medium" | "large";
	sx: SxProps<Theme>;
};

/** The bell as staff have always had it: the unseen-notification count, and a click opens `/notifications`. */
export const StaffNotificationBell: React.FC<Props> = ({ color, size, sx }) => {
	const { unseenNotificationCount } = useNotificationsContext();
	const navigate = useNavigate();
	const { userInfo } = useAuth();
	const previousCount = useRef<number>(unseenNotificationCount);

	useEffect(() => {
		previousCount.current = unseenNotificationCount;
	}, [unseenNotificationCount]);

	const handleClick = () => {
		trackEvent("notifications_bell_clicked", {
			user_id: userInfo?.id,
			unseen_count: unseenNotificationCount,
			has_notifications: unseenNotificationCount > 0,
			timestamp: new Date().toISOString(),
			referrer: window.location.pathname,
			session_id: sessionStorage.getItem("session_id") || "unknown",
		});

		navigate("/notifications");
	};

	return (
		<IconButton
			data-testid="header-notification-bell"
			onClick={handleClick}
			color={color}
			size={size}
			sx={sx}
			aria-label={`${unseenNotificationCount} unread notifications`}
		>
			<BellBadge badgeContent={unseenNotificationCount} invisible={unseenNotificationCount === 0} max={BELL_BADGE_MAX}>
				<NotificationsIcon fontSize={size} />
			</BellBadge>
		</IconButton>
	);
};
